import React, { useState, useEffect } from "react";
import { Outlet } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../store/authSlice";
import {
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Avatar,
  Divider,
  Menu,
  MenuItem,
  IconButton,
  AppBar,
  Toolbar,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Link, useLocation } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import LogoutIcon from "@mui/icons-material/Logout";
import MenuIcon from "@mui/icons-material/Menu";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const drawerWidth = 256;

const MainLayout = () => {
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (isMobile) setDrawerOpen(false);
  }, [location.pathname, isMobile]);

  useEffect(() => {
    if (!isMobile) setDrawerOpen(false);
  }, [isMobile]);

  const { user } = useSelector((state) => state.auth);

  const displayName =
    user?.name || (user?.email ? user.email.split("@")[0] : "User");

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleProfileClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const handleLogout = () => {
    try {
      dispatch(logout());
      if (window.localStorage) localStorage.clear();
      if (window.sessionStorage) sessionStorage.clear();
      const cookies = document.cookie.split(";");
      for (let i = 0; i < cookies.length; i++) {
        const cookie = cookies[i];
        const eqPos = cookie.indexOf("=");
        const name =
          eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
        document.cookie =
          name + "=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;";
      }
      window.sessionStorage.setItem("logoutRedirect", "true");
      handleClose();
      window.location.href = "/login";
    } catch (error) {
      console.error("Error during logout:", error);
      window.location.href = "/login";
    }
  };

  const menuItems = [
    { text: "Dashboard", path: "/", icon: <HomeIcon sx={{ fontSize: 20 }} /> },
    { text: "My Elections", path: "/my-elections", icon: <HowToVoteIcon sx={{ fontSize: 20 }} /> },
    { text: "Create Election", path: "/create-election", icon: <AddCircleOutlineIcon sx={{ fontSize: 20 }} /> },
  ];

  const isActive = (path) => {
    if (path === "/") return location.pathname === "/admin" || location.pathname === "/admin/";
    return location.pathname === `/admin${path}` || location.pathname === path;
  };

  const drawerContent = (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Logo */}
      <Box sx={{ px: 2.5, py: 2.5, display: "flex", alignItems: "center", gap: 1.5 }}>
        <img
          src="/images/logo.svg"
          alt="SafeBallot"
          style={{ width: 36, height: 36, objectFit: "contain" }}
        />
        <Box>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 700,
              color: "#013763",
              fontSize: "1rem",
              lineHeight: 1.2,
              letterSpacing: "-0.01em",
            }}
          >
            SafeBallot
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "#94A3B8",
              fontSize: "0.6rem",
              letterSpacing: "0.1em",
              fontWeight: 600,
              textTransform: "uppercase",
            }}
          >
            Secure Voting
          </Typography>
        </Box>
      </Box>

      <Divider sx={{ mx: 2, borderColor: "#F1F5F9" }} />

      {/* Navigation */}
      <List sx={{ px: 1.5, mt: 1.5, flex: 1 }}>
        {menuItems.map((item) => {
          const active = isActive(item.path);
          return (
            <ListItem
              key={item.text}
              component={Link}
              to={`/admin${item.path === "/" ? "" : item.path}`}
              onClick={() => { if (isMobile) setDrawerOpen(false); }}
              sx={{
                borderRadius: "8px",
                mb: 0.5,
                py: 1,
                px: 1.5,
                transition: "all 0.15s ease",
                backgroundColor: active ? "#013763" : "transparent",
                color: active ? "#FFFFFF" : "#475569",
                "&:hover": {
                  backgroundColor: active ? "#012347" : "#F1F5F9",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 32,
                  color: active ? "#FFFFFF" : "#94A3B8",
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.text}
                primaryTypographyProps={{
                  fontWeight: active ? 600 : 500,
                  fontSize: "0.85rem",
                }}
              />
              {active && (
                <ChevronRightIcon sx={{ fontSize: 16, opacity: 0.7 }} />
              )}
            </ListItem>
          );
        })}
      </List>

      <Divider sx={{ mx: 2, borderColor: "#F1F5F9" }} />

      {/* User profile */}
      <Box
        sx={{
          p: 2,
          mx: 1,
          mb: 1,
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          borderRadius: "8px",
          transition: "all 0.15s ease",
          "&:hover": { backgroundColor: "#F8FAFC" },
        }}
        onClick={handleProfileClick}
      >
        <Avatar
          src={user?.avatar}
          sx={{
            width: 34,
            height: 34,
            mr: 1.5,
            backgroundColor: "#013763",
            fontWeight: 600,
            fontSize: "0.85rem",
          }}
        >
          {!user?.avatar && displayName
            ? displayName.charAt(0).toUpperCase()
            : "U"}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            sx={{ fontWeight: 600, color: "#0F172A", lineHeight: 1.3, fontSize: "0.85rem" }}
            noWrap
          >
            {displayName}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: "#94A3B8", fontSize: "0.65rem" }}
            noWrap
          >
            Election Admin
          </Typography>
        </Box>
      </Box>

      <Menu
        id="profile-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transformOrigin={{ vertical: "bottom", horizontal: "center" }}
        PaperProps={{
          sx: {
            borderRadius: "10px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.08), 0 0 0 1px rgba(0,0,0,0.04)",
            minWidth: 180,
          },
        }}
      >
        <MenuItem
          onClick={handleLogout}
          sx={{ borderRadius: "6px", mx: 0.5, py: 1 }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" sx={{ color: "#DC2626" }} />
          </ListItemIcon>
          <ListItemText
            primaryTypographyProps={{ fontWeight: 500, fontSize: "0.85rem", color: "#DC2626" }}
          >
            Log out
          </ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#F7F8FA" }}>
      {/* Mobile App Bar */}
      {isMobile && (
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            backgroundColor: "#FFFFFF",
            borderBottom: "1px solid #E2E8F0",
          }}
        >
          <Toolbar sx={{ minHeight: 56 }}>
            <IconButton
              edge="start"
              onClick={() => setDrawerOpen(true)}
              sx={{ mr: 1.5, color: "#013763" }}
            >
              <MenuIcon />
            </IconButton>
            <img
              src="/images/logo.svg"
              alt="SafeBallot"
              style={{ width: 28, height: 28, objectFit: "contain", marginRight: 8 }}
            />
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: "#013763",
                fontSize: "0.95rem",
              }}
            >
              SafeBallot
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      <Drawer
        variant={isMobile ? "temporary" : "permanent"}
        open={isMobile ? drawerOpen : true}
        onClose={() => setDrawerOpen(false)}
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          "& .MuiDrawer-paper": {
            width: drawerWidth,
            boxSizing: "border-box",
            border: "none",
            backgroundColor: "#FFFFFF",
            borderRight: "1px solid #F1F5F9",
          },
        }}
        ModalProps={{ keepMounted: true }}
      >
        {drawerContent}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3, md: 4 },
          width: { xs: "100%", md: `calc(100% - ${drawerWidth}px)` },
          backgroundColor: "#F7F8FA",
          minHeight: "100vh",
          marginTop: isMobile ? "56px" : 0,
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
};

export default MainLayout;
